/**
 * Prompt Library endpoints — all URLs include trailing slash to avoid
 * FastAPI 307 redirects. If-Match is REQUIRED on every non-create mutation;
 * the backend returns 412 on a stale metadata_version. `list` uses
 * repeatedKeyParamsSerializer so that `status[]` serialises as repeated
 * params (?status=active&status=archived).
 */

import type {
  PromptBodyUpdate,
  PromptCreate,
  PromptListFilters,
  PromptListResponse,
  PromptMetadataUpdate,
  PromptResponse,
  PromptRevisionListResponse,
  PromptRevisionResponse,
  PromptRollbackRequest,
  PromptStatusTransition,
} from '@/types/prompts'
import { apiClient } from './client'
import { ifMatchHeaders } from './ifMatch'
import { repeatedKeyParamsSerializer } from './queryParams'

export const prompts = {
  list: async (f?: PromptListFilters): Promise<PromptListResponse> => {
    const response = await apiClient.get<PromptListResponse>(
      '/api/v1/prompts/',
      { params: f, paramsSerializer: repeatedKeyParamsSerializer }
    )
    return response.data
  },

  getById: async (id: string): Promise<PromptResponse> => {
    const response = await apiClient.get<PromptResponse>(
      `/api/v1/prompts/${id}/`
    )
    return response.data
  },

  getByNumber: async (n: number): Promise<PromptResponse> => {
    const response = await apiClient.get<PromptResponse>(
      `/api/v1/prompts/by-number/${n}/`
    )
    return response.data
  },

  // Resolves a portable deep-link title to its canonical prompt. Non-unique
  // handle; the backend applies the deterministic visibility tiebreak.
  getByTitle: async (title: string): Promise<PromptResponse> => {
    const response = await apiClient.get<PromptResponse>(
      `/api/v1/prompts/by-title/${encodeURIComponent(title)}/`
    )
    return response.data
  },

  create: async (data: PromptCreate): Promise<PromptResponse> => {
    const response = await apiClient.post<PromptResponse>(
      '/api/v1/prompts/',
      data
    )
    return response.data
  },

  // If-Match is required on every mutation below — the backend declares it
  // Annotated[int, Header(alias="If-Match")] and returns 422 when absent.
  updateMetadata: async (
    id: string,
    data: PromptMetadataUpdate,
    ifMatch: number
  ): Promise<PromptResponse> => {
    const response = await apiClient.patch<PromptResponse>(
      `/api/v1/prompts/${id}/`,
      data,
      ifMatchHeaders(ifMatch)
    )
    return response.data
  },

  updateBody: async (
    id: string,
    data: PromptBodyUpdate,
    ifMatch: number
  ): Promise<PromptResponse> => {
    const response = await apiClient.patch<PromptResponse>(
      `/api/v1/prompts/${id}/body/`,
      data,
      ifMatchHeaders(ifMatch)
    )
    return response.data
  },

  setStatus: async (
    id: string,
    data: PromptStatusTransition,
    ifMatch: number
  ): Promise<PromptResponse> => {
    const response = await apiClient.put<PromptResponse>(
      `/api/v1/prompts/${id}/status/`,
      data,
      ifMatchHeaders(ifMatch)
    )
    return response.data
  },

  remove: async (id: string, ifMatch: number): Promise<void> => {
    await apiClient.delete(`/api/v1/prompts/${id}/`, ifMatchHeaders(ifMatch))
  },

  restore: async (id: string, ifMatch: number): Promise<PromptResponse> => {
    const response = await apiClient.post<PromptResponse>(
      `/api/v1/prompts/${id}/restore/`,
      null,
      ifMatchHeaders(ifMatch)
    )
    return response.data
  },

  listRevisions: async (
    id: string,
    opts?: { skip?: number; limit?: number }
  ): Promise<PromptRevisionListResponse> => {
    const response = await apiClient.get<PromptRevisionListResponse>(
      `/api/v1/prompts/${id}/revisions/`,
      { params: { skip: opts?.skip ?? 0, limit: opts?.limit ?? 50 } }
    )
    return response.data
  },

  getRevision: async (
    id: string,
    revId: string
  ): Promise<PromptRevisionResponse> => {
    const response = await apiClient.get<PromptRevisionResponse>(
      `/api/v1/prompts/${id}/revisions/${revId}/`
    )
    return response.data
  },

  // If-Match is required — rollback is a mutation; 412 on stale metadata_version.
  rollback: async (
    id: string,
    data: PromptRollbackRequest,
    ifMatch: number
  ): Promise<PromptResponse> => {
    const response = await apiClient.post<PromptResponse>(
      `/api/v1/prompts/${id}/rollback/`,
      data,
      ifMatchHeaders(ifMatch)
    )
    return response.data
  },
}
